import camelCase from 'lodash.camelcase';
import upperFirst from 'lodash.upperfirst';
import { SECTION_CODES, BASE_SECTION_CODES } from '@/constants';
import {
  getPathFromTokens,
  getLabelForSection,
  getSectionKey,
  getOptions,
  getSectionId,
  getBaseToken,
} from './index';
import { transformStringToObject as transformInterestStringToObject } from './transformers/interestsTransformers';

export function decodeAgeRange(value) {
  const [month, year] = value.split(', ');
  return { month, year };
}

function encodeAgeRange(month, year) {
  return `${month}, ${year}`;
}

export function processBooleanOrReturnValue(key, value) {
  const bool = typeof value === 'boolean';

  if (bool) {
    return value === true ? key : null;
  }

  return value;
}

function transformInterestsToString(key, value) {
  return Object.entries(value).map((entry) => processBooleanOrReturnValue(...entry));
}

function transformItemToString(key, value) {
  if (getBaseToken(key) === BASE_SECTION_CODES.INTERESTS) {
    return transformInterestsToString(key, value);
  }

  switch (key) {
    case SECTION_CODES.AGE_RANGE: {
      const { month, year } = decodeAgeRange(value);
      return `${key}|${month}|${year}`;
    }
    case SECTION_CODES.POST_CODE: {
      const postcode = value.trim();
      return `${key}|${postcode}`;
    }
    case SECTION_CODES.COUNTRY:
      return `${getPathFromTokens([key, value.geonameId])}|${value.countryName}`;
    case SECTION_CODES.CITY:
      return `${getPathFromTokens([key, value.cityGeonameId])}|${value.cityName}, ${
        value.countryName
      }`;
    default:
      return processBooleanOrReturnValue(key, value);
  }
}

export const transformTargetInfoObjectToArray = (data) =>
  Array.from(
    new Set(
      Object.entries(data)
        .filter(([, value]) => value)
        .map((entry) => transformItemToString(...entry))
        .flat()
        .filter(Boolean),
    ),
  );

function getItemId(key, sectionKey) {
  return key.substr(sectionKey.length + 1);
}

function transformStringToObject(value) {
  const [key, val, ...tokens] = value.split('|');

  if (!key) {
    return {};
  }

  const sectionKey = getSectionKey(key);

  if (val) {
    switch (sectionKey) {
      case SECTION_CODES.COUNTRY:
        return {
          [sectionKey]: {
            geonameId: getItemId(key, sectionKey),
            countryName: val,
          },
        };
      case SECTION_CODES.CITY: {
        const [cityName, countryName] = val.split(', ');
        return {
          [sectionKey]: {
            cityGeonameId: getItemId(key, sectionKey),
            countryName,
            cityName,
          },
        };
      }
      case SECTION_CODES.AGE_RANGE: {
        const month = val;
        const [year] = tokens;
        return {
          [sectionKey]: encodeAgeRange(month, year),
        };
      }
      default:
        return {
          [key]: val,
        };
    }
  }

  if (getBaseToken(sectionKey) === BASE_SECTION_CODES.INTERESTS) {
    return transformInterestStringToObject(key);
  }

  return {
    [sectionKey]: sectionKey === key || key,
  };
}

export const transformTargetInfoArrayToObject = (data) =>
  data.map(transformStringToObject).reduce((results, transformedItem) => {
    const [[key, value]] = Object.entries(transformedItem);
    const oldVal = results[key];
    const newVal =
      typeof oldVal === 'object'
        ? {
            ...oldVal,
            ...value,
          }
        : value;

    return Object.assign(results, {
      [key]: newVal,
    });
  }, {});

function ignoreTransformation() {
  return {};
}

function getTrackTransformersBySectionType(key, scheme) {
  const map = {
    [SECTION_CODES.AGE_RANGE]: (value) => {
      const { month, year } = decodeAgeRange(value);
      return {
        YearOfBirth: year,
        MonthOfBirth: month,
      };
    },
    [SECTION_CODES.COUNTRY]: (value) => {
      return {
        Country: value.countryName,
      };
    },
    [SECTION_CODES.TEST_ACCOUNT]: ignoreTransformation,
    [SECTION_CODES.POST_CODE]: (value) => ({ zipCode: value }),
  };

  function getDefaultTransformer(value) {
    const section = scheme.find((item) => getSectionId(item) === key);

    if (!section) {
      return ignoreTransformation();
    }

    const trackKey = upperFirst(camelCase(section.displayName));
    const trackValue =
      getLabelForSection((getOptions(section)?.find((node) => node[value]) || {})[value]) ?? value;

    return {
      [trackKey]: trackValue,
    };
  }

  return map[getSectionKey(key)] || getDefaultTransformer;
}

export const transformToTrackObject = (data, displayData = []) => {
  const tracks = Object.entries(data).reduce((results, [key, value]) => {
    const transformer = getTrackTransformersBySectionType(key, displayData);

    if (!transformer) {
      return results;
    }

    try {
      return Object.assign(results, transformer(value));
    } catch {
      console.error(`Track transformation for key:${key} failed`);
      return results;
    }
  }, {});

  return tracks;
};
